import React from "react";
// Assuming these image paths are correctly resolved by your build system
import valveImg from "@images/categories/valves.jpg";
import actuatorImg from "@images/categories/actuators.jpg";
import automationImg from "@images/categories/automation.jpeg";
import sensorsImg from "@images/categories/sensors.jpg";
import pipeImg from "@images/categories/pipe-flanges.jpg";
import electricImg from "@images/categories/electric.jpeg";

interface CategoryGridProps {
  categories: { name: string; subcategories?: string[] }[];
  onCategorySelect: (category: string) => void;
}

const CategoryGrid: React.FC<CategoryGridProps> = ({
  categories,
  onCategorySelect,
}) => {
  // Map of category names to images. Using Record for better type safety.
  const categoryImages: Record<string, string> = {
    Válvulas: valveImg,
    "Atuadores Pneumáticos": actuatorImg,
    "Automação e Controle": automationImg,
    "Sensores e Gateways para Monitoramento Industrial": sensorsImg,
    "Conexões e Flanges para Tubulação": pipeImg,
    "Painéis Elétricos": electricImg,
    // Fallback image for categories not explicitly mapped, enhancing robustness.
    default: "https://via.placeholder.com/300/CCCCCC/FFFFFF?text=No+Image",
  };

  // Handles keyboard interaction for accessibility.
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    categoryName: string,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault(); // Prevent default space scroll
      onCategorySelect(categoryName);
    }
  };

  return (
    <div className="flex h-full w-full flex-wrap items-start justify-center bg-gray-100 p-4 sm:p-6 md:p-8">
      {categories.map((category) => (
        <div
          key={`group-category-${category.name}`}
          // Responsive width adjustments for various screen sizes. Max width ensures cards don't get too large.
          className="w-full cursor-pointer p-2 sm:w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/5"
          style={{ maxWidth: "280px" }} // Constrains card width on larger screens for aesthetics
        >
          <div
            role="button"
            tabIndex={0}
            className="/* Enhanced hover effect */ /* Consistent focus effect */ group relative aspect-square transform overflow-hidden rounded-xl bg-white shadow-lg outline-none transition-all duration-300 ease-in-out hover:-translate-y-1.5 hover:scale-105 hover:shadow-2xl focus-visible:-translate-y-1.5 focus-visible:scale-105 focus-visible:shadow-2xl focus-visible:ring-4 focus-visible:ring-blue-500 focus-visible:ring-opacity-75"
            onClick={() => onCategorySelect(category.name)}
            onKeyDown={(e) => handleKeyDown(e, category.name)}
            aria-label={category.name}
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110 group-focus-visible:scale-110"
              style={{
                backgroundImage: `url(${
                  categoryImages[category.name] || categoryImages.default
                })`,
              }}
            />
            {/* Gradient overlay for improved text legibility and modern aesthetics. */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/50 to-transparent opacity-90 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100" />

            {/* Text content aligned to the bottom for a cleaner look. */}
            <div className="absolute inset-0 flex flex-col items-center justify-end p-4 text-center md:p-5">
              <h3 className="text-base font-semibold text-white drop-shadow-md transition-all duration-300 group-hover:drop-shadow-lg md:text-lg">
                {" "}
                {/* Text shadow for better contrast. */}
                {category.name}
              </h3>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CategoryGrid;
